import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";

// Administrative Templates (ADMX) editor — the Group Policy Management
// Editor experience: a category tree on the left, the policies in the
// selected category (or search results) in the middle, and a policy
// editor (Not Configured / Enabled / Disabled + presentation elements)
// on the right. Backed by the domain's real ADMX store via the AD runner.

interface AdmxCategory {
  id: string;
  display: string;
  parent: string | null;
}
interface AdmxItem {
  display: string;
  value: { type: string; value: unknown } | null;
}
interface AdmxElement {
  kind: string;
  id: string;
  valueName?: string;
  label?: string;
  items?: AdmxItem[];
  minValue?: number;
  maxValue?: number;
  defaultItem?: number;
  defaultChecked?: boolean;
  defaultText?: string;
  storeAsText?: boolean;
  expandable?: boolean;
}
interface AdmxPolicy {
  id: string;
  name: string;
  display: string;
  explain: string;
  class: string; // Machine | User | Both
  category: string | null;
  key?: string;
  valueName?: string;
  elements: AdmxElement[];
}
interface Catalog {
  meta: { source?: string; language?: string; policy_count?: number; category_count?: number };
  categories: AdmxCategory[];
  policies: AdmxPolicy[];
}

const CARD =
  "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const INPUT =
  "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm";

const EDITABLE_KINDS = new Set(["boolean", "decimal", "longDecimal", "text", "multiText", "enum"]);

export default function AdmxEditor({
  customerId,
  gpoGuid,
  gpoName,
}: {
  customerId: number;
  gpoGuid: string;
  gpoName: string;
}) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<AdmxPolicy | null>(null);

  // Policy editor state
  const [state, setState] = useState<"enabled" | "disabled" | "not_configured">("not_configured");
  const [section, setSection] = useState<"machine" | "user">("machine");
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/api/v1/gpo/${customerId}/admx/catalog`);
      setCatalog(r.data);
      setNeedRefresh(false);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      if (err.response?.status === 404) {
        setNeedRefresh(true);
        setCatalog(null);
      } else {
        setError(err.response?.data?.detail || "Couldn't load Administrative Templates.");
      }
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const refresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      await api.post(`/api/v1/gpo/${customerId}/admx/refresh`, { language: "en-US" });
      await loadCatalog();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Couldn't fetch the ADMX store from the runner.");
    } finally {
      setRefreshing(false);
    }
  };

  // Build category tree + policy indexes.
  const { childrenOf, roots, policiesByCat, catHasPolicies } = useMemo(() => {
    const children: Record<string, AdmxCategory[]> = {};
    const byId: Record<string, AdmxCategory> = {};
    const polByCat: Record<string, AdmxPolicy[]> = {};
    const cats = catalog?.categories || [];
    const pols = catalog?.policies || [];
    cats.forEach((c) => (byId[c.id] = c));
    cats.forEach((c) => {
      const p = c.parent && byId[c.parent] ? c.parent : "__root__";
      (children[p] = children[p] || []).push(c);
    });
    pols.forEach((p) => {
      const cid = p.category || "__uncategorized__";
      (polByCat[cid] = polByCat[cid] || []).push(p);
    });
    Object.values(children).forEach((arr) => arr.sort((a, b) => a.display.localeCompare(b.display)));
    Object.values(polByCat).forEach((arr) => arr.sort((a, b) => a.display.localeCompare(b.display)));
    // Categories whose subtree contains at least one policy (prune empties).
    const has = new Set<string>();
    const visit = (id: string): boolean => {
      let any = (polByCat[id]?.length || 0) > 0;
      for (const c of children[id] || []) if (visit(c.id)) any = true;
      if (any) has.add(id);
      return any;
    };
    (children["__root__"] || []).forEach((c) => visit(c.id));
    return {
      childrenOf: children,
      roots: (children["__root__"] || []).filter((c) => has.has(c.id)),
      policiesByCat: polByCat,
      catHasPolicies: has,
    };
  }, [catalog]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !catalog) return null;
    return catalog.policies
      .filter((p) => p.display.toLowerCase().includes(q))
      .slice(0, 300)
      .sort((a, b) => a.display.localeCompare(b.display));
  }, [search, catalog]);

  const openPolicy = (p: AdmxPolicy) => {
    setSel(p);
    setState("not_configured");
    setSection(p.class.toLowerCase() === "user" ? "user" : "machine");
    setApplyMsg("");
    // Seed element defaults.
    const seed: Record<string, unknown> = {};
    p.elements.forEach((e) => {
      if (e.kind === "boolean") seed[e.id] = !!e.defaultChecked;
      else if (e.kind === "enum") seed[e.id] = e.defaultItem ?? 0;
      else if (e.kind === "decimal" || e.kind === "longDecimal")
        seed[e.id] = e.defaultText ? Number(e.defaultText) : (e.minValue ?? 0);
      else seed[e.id] = e.defaultText ?? "";
    });
    setValues(seed);
  };

  const apply = async () => {
    if (!sel) return;
    setApplying(true);
    setApplyMsg("");
    try {
      const r = await api.post(`/api/v1/gpo/${customerId}/admx/apply`, {
        guid: gpoGuid,
        policy_id: sel.id,
        state,
        section,
        values: state === "enabled" ? values : {},
      });
      // Poll the command to completion so the tech sees a real result.
      const id = r.data.command_id;
      let done = false;
      for (let i = 0; i < 24 && !done; i++) {
        await new Promise((res) => setTimeout(res, 2500));
        const p = await api.get(`/api/v1/gpo/${customerId}/commands/${id}`);
        if (p.data.status === "completed") {
          setApplyMsg(`Applied — ${sel.display} set to ${state.replace("_", " ")}.`);
          done = true;
        } else if (p.data.status === "failed" || p.data.status === "expired") {
          throw new Error(p.data.error_message || "Apply failed");
        }
      }
      if (!done) setApplyMsg("Queued — the runner is taking longer than expected.");
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setApplyMsg("Error: " + (err.response?.data?.detail || err.message || "apply failed"));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-400 dark:text-gray-500">Loading Administrative Templates…</p>;
  }

  if (needRefresh) {
    return (
      <div className={`${CARD} p-4`}>
        <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
          Administrative Templates aren't loaded for this domain yet.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
          This reads the domain's ADMX policy definitions from the AD runner (central SYSVOL store
          if present, otherwise the DC's local store) and caches them. It can take up to a minute.
        </p>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {refreshing ? "Loading from runner…" : "Load Administrative Templates"}
        </button>
        {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
      </div>
    );
  }

  const listPolicies = searchResults ?? (selectedCat ? policiesByCat[selectedCat] || [] : []);
  const polCount = catalog?.policies.length ?? 0;
  const catCount = catalog?.categories.length ?? 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <span className="font-semibold text-gray-700 dark:text-gray-200">
            {polCount.toLocaleString()} settings
          </span>{" "}
          across {catCount} categories{catalog?.meta.language ? ` · ${catalog.meta.language}` : ""} — search
          or expand a category to edit any of them.
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 shrink-0"
        >
          {refreshing ? "Refreshing…" : "Refresh templates"}
        </button>
      </div>

      <input
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setSel(null);
        }}
        placeholder={`Search all ${polCount.toLocaleString()} settings by name…`}
        className={`${INPUT} w-full mb-2`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-3">
        {/* Left: category tree (hidden while searching) */}
        {!searchResults && (
          <div className={`${CARD} p-2 h-[460px] overflow-auto`}>
            {roots.map((c) => (
              <TreeNode
                key={c.id}
                cat={c}
                depth={0}
                childrenOf={childrenOf}
                catHasPolicies={catHasPolicies}
                expanded={expanded}
                setExpanded={setExpanded}
                selectedCat={selectedCat}
                onSelect={(id) => {
                  setSelectedCat(id);
                  setSel(null);
                }}
              />
            ))}
          </div>
        )}

        {/* Right: policy list + editor */}
        <div className={`${CARD} p-3 h-[460px] overflow-auto ${searchResults ? "lg:col-span-2" : ""}`}>
          {sel ? (
            <PolicyEditor
              policy={sel}
              state={state}
              setState={setState}
              section={section}
              setSection={setSection}
              values={values}
              setValues={setValues}
              onBack={() => setSel(null)}
              onApply={apply}
              applying={applying}
              applyMsg={applyMsg}
              gpoName={gpoName}
            />
          ) : listPolicies.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {searchResults ? "No settings match." : "Select a category to see its settings."}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {listPolicies.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => openPolicy(p)}
                    className="w-full text-left py-1.5 px-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded flex items-center gap-2"
                  >
                    <span className="text-sm text-gray-800 dark:text-gray-100 flex-1">{p.display}</span>
                    <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500 shrink-0">
                      {p.class}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TreeNode({
  cat,
  depth,
  childrenOf,
  catHasPolicies,
  expanded,
  setExpanded,
  selectedCat,
  onSelect,
}: {
  cat: AdmxCategory;
  depth: number;
  childrenOf: Record<string, AdmxCategory[]>;
  catHasPolicies: Set<string>;
  expanded: Set<string>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedCat: string | null;
  onSelect: (id: string) => void;
}) {
  const kids = (childrenOf[cat.id] || []).filter((c) => catHasPolicies.has(c.id));
  const isOpen = expanded.has(cat.id);
  const toggle = () =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(cat.id) ? next.delete(cat.id) : next.add(cat.id);
      return next;
    });
  return (
    <div>
      <div
        className={`flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer ${
          selectedCat === cat.id ? "bg-blue-50 dark:bg-blue-900/30" : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        }`}
        style={{ paddingLeft: depth * 12 + 4 }}
        onClick={() => onSelect(cat.id)}
      >
        {kids.length > 0 ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            className="w-4 text-gray-400 dark:text-gray-500 text-xs"
          >
            {isOpen ? "▾" : "▸"}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{cat.display}</span>
      </div>
      {isOpen &&
        kids.map((c) => (
          <TreeNode
            key={c.id}
            cat={c}
            depth={depth + 1}
            childrenOf={childrenOf}
            catHasPolicies={catHasPolicies}
            expanded={expanded}
            setExpanded={setExpanded}
            selectedCat={selectedCat}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

function PolicyEditor({
  policy,
  state,
  setState,
  section,
  setSection,
  values,
  setValues,
  onBack,
  onApply,
  applying,
  applyMsg,
  gpoName,
}: {
  policy: AdmxPolicy;
  state: "enabled" | "disabled" | "not_configured";
  setState: (s: "enabled" | "disabled" | "not_configured") => void;
  section: "machine" | "user";
  setSection: (s: "machine" | "user") => void;
  values: Record<string, unknown>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onBack: () => void;
  onApply: () => void;
  applying: boolean;
  applyMsg: string;
  gpoName: string;
}) {
  const setVal = (id: string, v: unknown) => setValues((prev) => ({ ...prev, [id]: v }));
  const isBoth = policy.class.toLowerCase() === "both";
  const editable = policy.elements.filter((e) => EDITABLE_KINDS.has(e.kind));
  const skipped = policy.elements.filter((e) => !EDITABLE_KINDS.has(e.kind));

  return (
    <div>
      <button onClick={onBack} className="text-xs text-blue-600 dark:text-blue-400 hover:underline mb-2">
        ← All settings
      </button>
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{policy.display}</h4>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-2">
        Editing in <span className="font-medium">{gpoName}</span>
      </p>

      {isBoth && (
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 mb-2">
          Applies to
          <select value={section} onChange={(e) => setSection(e.target.value as "machine" | "user")} className={INPUT}>
            <option value="machine">Computer Configuration</option>
            <option value="user">User Configuration</option>
          </select>
        </label>
      )}

      <div className="flex items-center gap-3 mb-3">
        {(["not_configured", "enabled", "disabled"] as const).map((s) => (
          <label key={s} className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
            <input type="radio" checked={state === s} onChange={() => setState(s)} />
            {s === "not_configured" ? "Not Configured" : s.charAt(0).toUpperCase() + s.slice(1)}
          </label>
        ))}
      </div>

      {state === "enabled" && editable.length > 0 && (
        <div className="space-y-2 mb-3 border-l-2 border-gray-100 dark:border-gray-700 pl-3">
          {editable.map((e) => (
            <div key={e.id} className="text-sm">
              <label className="block text-xs text-gray-600 dark:text-gray-300 mb-0.5">
                {e.label || e.valueName || e.id}
              </label>
              {e.kind === "boolean" ? (
                <input
                  type="checkbox"
                  checked={!!values[e.id]}
                  onChange={(ev) => setVal(e.id, ev.target.checked)}
                />
              ) : e.kind === "enum" ? (
                <select
                  value={Number(values[e.id] ?? 0)}
                  onChange={(ev) => setVal(e.id, Number(ev.target.value))}
                  className={`${INPUT} w-full`}
                >
                  {(e.items || []).map((it, i) => (
                    <option key={i} value={i}>
                      {it.display}
                    </option>
                  ))}
                </select>
              ) : e.kind === "decimal" || e.kind === "longDecimal" ? (
                <input
                  type="number"
                  min={e.minValue}
                  max={e.maxValue}
                  value={Number(values[e.id] ?? 0)}
                  onChange={(ev) => setVal(e.id, Number(ev.target.value))}
                  className={`${INPUT} w-40`}
                />
              ) : (
                <input
                  type="text"
                  value={String(values[e.id] ?? "")}
                  onChange={(ev) => setVal(e.id, ev.target.value)}
                  className={`${INPUT} w-full`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {state === "enabled" && skipped.length > 0 && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-2">
          {skipped.length} option{skipped.length > 1 ? "s" : ""} in this policy (list/multi-value) aren't
          editable here yet — use the raw registry editor for those.
        </p>
      )}

      {policy.explain && (
        <details className="mb-3">
          <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">Help</summary>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 whitespace-pre-wrap">{policy.explain}</p>
        </details>
      )}

      <button
        onClick={onApply}
        disabled={applying}
        className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {applying ? "Applying…" : "Apply"}
      </button>
      {applyMsg && (
        <p
          className={`text-xs mt-2 ${
            applyMsg.startsWith("Error") ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"
          }`}
        >
          {applyMsg}
        </p>
      )}
    </div>
  );
}
